import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Typography, Row, Col, Breadcrumb } from 'antd';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { PreHeader } from '../components/PreHeader';
import { Header } from '../sections/Header';
import { Footer } from '../sections/Footer';
import { MissionStepper } from '../components/MissionStepper';
import { MissionCard } from '../components/MissionCard';
import { MissionDisplay } from '../components/MissionDisplay';
import { MissionProvider } from '../hooks/useMissionState';
import { db } from '../services/database';
import { standardVariants } from '../utils/useAnimations';
import { useAccessibility } from '../utils/accessibility';
import * as LucideIcons from 'lucide-react';
import type { ParcoursWithRelations } from '../types/database';

const { Content } = Layout;
const { Title } = Typography;

export const ParcoursPage: React.FC = () => {
  const { slug, missionId } = useParams<{ slug: string; missionId?: string }>();
  const navigate = useNavigate();
  const { getText } = useAccessibility();

  const [parcours, setParcours] = useState<ParcoursWithRelations | null>(null);
  const [currentMissionIndex, setCurrentMissionIndex] = useState(0);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(true);
  
  useEffect(() => {
    console.log("hidden page",hidden);
    
    // if (!hidden) setHidden(true);
  }, [hidden]);

  useEffect(() => {
    
    const loadParcours = async () => {
      if (slug) {
        try {
          const parcoursData = await db.getParcoursWithRelationsBySlug(slug);
          if (parcoursData) {
            setParcours(parcoursData);
            
            // Déterminer la mission actuelle
            if (missionId) {
              const missionIndex = parcoursData.missions.findIndex(m => m.id === missionId);
              if (missionIndex !== -1) {
                setCurrentMissionIndex(missionIndex);
              }
            }
          }
        } catch (error) {
          console.error('Erreur lors du chargement du parcours:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    loadParcours();
  }, [slug, missionId]);

  const handleToggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const handleToggleFooter = () => {
    setIsFooterVisible(!isFooterVisible);
  };

  const handleStartMission = () => {
    console.log('Démarrage de la mission:', parcours?.missions[currentMissionIndex]);
  };

  const handleBackToParcours = () => {
    navigate('/#parcours');
  };

  if (loading) {
    return (
      <Layout className="min-h-screen">
        <Content className="flex items-center justify-center">
          <div>Chargement du parcours...</div>
        </Content>
      </Layout>
    );
  }

  if (!parcours) {
    return (
      <Layout className="min-h-screen">
        <Content className="flex items-center justify-center">
          <div className="text-center">
            <Title level={2}>Parcours non trouvé</Title>
            <button onClick={handleBackToParcours} className="btn-primary">
              Retour aux parcours
            </button>
          </div>
        </Content>
      </Layout>
    );
  }

  const currentMission = parcours.missions[currentMissionIndex];
  const TypeIcon = LucideIcons[parcours.typesParcours[0]?.picto as keyof typeof LucideIcons] as React.ComponentType<any>;

  return ( 
    <>
    <MissionProvider>
      <Layout className="min-h-screen bg-neutral-white">
        <PreHeader
          onToggleMenu={handleToggleMenu}
          onToggleFooter={handleToggleFooter}
          isMenuVisible={isMenuVisible}
          isFooterVisible={isFooterVisible}
        />

        {isMenuVisible && (
          <div className="my-12"> 
            <Header />
          </div>
        )}

        <Content className={`z-[1] ${isMenuVisible ? 'mt-12' : 'mt-12'}`}>
          {/* Breadcrumb */}
          <div className="px-10 py-4">
            <div className="max-w-[1440px] mx-auto">
              <button
                onClick={handleBackToParcours}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-800 bg-transparent border-none cursor-pointer text-base"
                aria-label={getText('navigation.backToCourses')}
              >
                <ArrowLeft className="w-4 h-4" />
                Retour aux parcours
              </button>
            </div>
          </div>

          {/* Titre du parcours */}
          <motion.div
            id="main-content"
            className="px-10 pt-6 bg-transparent"
            variants={standardVariants}
            initial="hidden"
            animate="visible"
            tabIndex={-1}
          >
            <div className="max-w-[1440px] mx-auto">
              <div className="flex items-center gap-3 mb-2">
                <Title level={1} className="!text-3xl !font-bold !mb-0">
                  Parcours {parcours.id.split("-")[1]} : {parcours.nom}
                </Title>
                <div className={`tag tag-${parcours.typesParcours[0]?.classe} flex items-center gap-1 text-nowrap mb-0`}>
                  {TypeIcon && <TypeIcon className="w-4 h-4" />}
                  {parcours.typesParcours[0]?.nom}
                </div>
              </div>
            </div>
          </motion.div>

          { parcours.missions.length > 1 ? (
            <>
            {/* Stepper */}
            <MissionStepper
              missions={parcours.missions}
              currentMissionIndex={currentMissionIndex}
            />
            </>
          ) : (
            <>
            {/* Sous-titre de la mission */}
            <div className="px-10 pt-2">
              <div className="max-w-[1440px] mx-auto">
                <div className="text-lg font-bold">
                  {currentMission.nom}
                </div>
              </div>
            </div>
            </>
          )}
          
          {/* Contenu principal */}
          <div className="px-10 py-6">
            <div className="max-w-[1440px] mx-auto">
              <Row gutter={[40, 40]} className="min-h-[500px]">
                {/* Colonne gauche - Mission */}
                <Col xs={24} lg={10}>
                  <motion.div
                    variants={standardVariants}
                    initial="hidden"
                    animate="visible"
                    className="h-full border-none"
                  >
                    <MissionCard
                      hidden={hidden}
                      setHidden={setHidden}
                      mission={currentMission}
                      onStartMission={handleStartMission}
                    />
                  </motion.div>
                </Col>

                {/* Colonne droite - Zone de mission */}
                <Col xs={24} lg={14}>
                  <motion.div
                    variants={standardVariants}
                    initial="hidden"
                    animate="visible"
                    className="h-full"
                  >
                    <MissionDisplay 
                      hidden={hidden}
                      setHidden={setHidden}
                      mission={currentMission}
                      parcours={parcours}
                      currentMissionIndex={currentMissionIndex}
                    />
                  </motion.div>
                </Col>
              </Row>
            </div>
          </div>
        </Content>
            
        <div className="blur-2xl overflow-hidden absolute h-full w-full inset-0 z-0 mb-0">
          <img alt='' className="w-[468px] absolute -bottom-60 -left-20 opacity-50 " src="/img/svg/shapes/donut.svg" />
          <img alt='' className="w-[468px] absolute -top-[8%] -right-20 opacity-50 " src="/img/svg/shapes/half_donut.svg" />
        </div>  
        
        <div className="z-[1]" id="site-footer" tabIndex={-1}>
          {isFooterVisible && (<Footer />)}
        </div>
        
      </Layout>
    </MissionProvider>
    </>
  );
};